import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  styled,
  Typography,
} from '@mui/material';
import { Mail, Phone } from '@mui/icons-material';
import Bluebird from 'bluebird';
import bannerImg from '../../assets/images/contact-us.png';
import PagePaper from '../../components/Common/PagePaper';
import { Colors } from '../../assets/theme/Colors';
import useActiveSite from '../../hooks/useActiveSite';
import useHasActiveFeatureFlag from '../../hooks/useFeatureFlags';
import { FF_SD_DASHBOARD_SETTINGS_PHONE_NUMBERS } from '../../components/FeatureFlagWrapper';
import { getSiteContactInfo } from '../../api/sites';

const Banner = () => (
  <Box
    sx={(theme) => ({
      marginBottom: 8,
      [theme.breakpoints.only('xs')]: {
        marginBottom: 2,
      },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '15vh',
      minHeight: 100,
      padding: '1em',
      boxSizing: 'border-box',
      color: 'white',
      position: 'relative',
      background: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url(${bannerImg}) no-repeat center center`,
      backgroundSize: 'cover',
    })}
  >
    <Grid
      sx={{
        textShadow: '1px 1px 2px black',
        textAlign: 'center',
        color: '#fff',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
    >
      <Grid item>
        <Typography variant="h4">Questions or comments?</Typography>
      </Grid>
      <Grid item>
        <Typography variant="h4">Get in touch</Typography>
      </Grid>
    </Grid>
  </Box>
);

const StyledAvatar = styled(Avatar)(() => ({
  backgroundColor: Colors.appBar.main,
  borderRadius: '10px',
  boxShadow: '0px 0px 0px 1px #fff inset',
  border: `${Colors.appBar.main} solid 2px`,
}));

const ContactLocalSiteDetails = () => {
  const activeSite = useActiveSite();
  const hasPhoneNumbersFF = useHasActiveFeatureFlag(FF_SD_DASHBOARD_SETTINGS_PHONE_NUMBERS);
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    if (!activeSite?.id) {
      return;
    }

    Bluebird.try(() => getSiteContactInfo(activeSite.id))
      .then((res) => res.json())
      .then((json) => setContactInfo(json.data));
  }, [activeSite?.id]);

  if (!contactInfo) {
    return null;
  }

  const phoneDigits = contactInfo.phone_number
    ? contactInfo.phone_number.replace(/\D/g, '')
    : null;

  return (
    <>
      <Banner />
      <PagePaper>
        <Container maxWidth="md">
          <Grid px={{ xs: 2, sm: 5, md: 10, lg: 20 }}>
            <Grid container direction="column" justifyContent="center" alignItems="flex-start" spacing={1}>
              <Grid item>
                <Typography variant="h5">Contact Details</Typography>
              </Grid>

              <Grid item>
                <List>
                  {hasPhoneNumbersFF && contactInfo.phone_number && (
                    <ListItem alignItems="center" dense>
                      <ListItemAvatar>
                        <StyledAvatar>
                          <Phone />
                        </StyledAvatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" color="textSecondary" display="block">
                            Phone
                          </Typography>
                        }
                        secondary={
                          <Typography
                            component={Link}
                            href={`tel:${phoneDigits}`}
                            variant="subtitle1"
                            color="textPrimary"
                            display="block"
                          >
                            {contactInfo.phone_number}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}

                  {contactInfo.site_directors.map((sd) => (
                    <ListItem key={sd.email} alignItems="center" dense>
                      <ListItemAvatar>
                        <StyledAvatar>
                          <Mail />
                        </StyledAvatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" color="textSecondary" display="block">
                            Email
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="textPrimary" display="block">
                              {sd.name} - Site Director
                            </Typography>
                            <Typography
                              component={Link}
                              href={`mailto:${sd.email}`}
                              variant="subtitle1"
                              display="block"
                            >
                              {sd.email}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}

                  {contactInfo.site_coordinators.map((sc) => (
                    <ListItem key={sc.email} alignItems="center" dense>
                      <ListItemAvatar>
                        <StyledAvatar>
                          <Mail />
                        </StyledAvatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" color="textSecondary" display="block">
                            Email
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="textPrimary" display="block">
                              {sc.name} - Site Coordinator
                            </Typography>
                            <Typography
                              component={Link}
                              href={`mailto:${sc.email}`}
                              variant="subtitle1"
                              display="block"
                            >
                              {sc.email}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </PagePaper>
    </>
  );
};

export default ContactLocalSiteDetails;
